import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { TILE_ENTITY_CONFIG } from "./config";
import { type OverviewTile, type TileAction } from "./types";

interface TileCardProps {
  tile: OverviewTile;
}

function ViewLabel() {
  const { t } = useTranslation();

  return (
    <>
      {t("home.manager.overview.view")}
      <ChevronRight className="size-4" aria-hidden="true" />
    </>
  );
}

function ViewAction({ action }: { action: TileAction }) {
  return (
    <WFPButton variant="link" size="sm" asChild className="shrink-0 px-0">
      <Link to={action.to} aria-label={action.ariaLabel}>
        <ViewLabel />
      </Link>
    </WFPButton>
  );
}

export function TileCard({ tile }: TileCardProps) {
  const { t } = useTranslation();
  const entity = TILE_ENTITY_CONFIG[tile.entityType];

  return (
    <Card className="gap-0 p-0">
      <div className="flex items-center gap-3 p-4 pb-0">
        <span
          className={cn(
            "inline-flex size-10 shrink-0 items-center justify-center rounded-md",
            entity.badgeClassName
          )}
        >
          <entity.icon className="size-5" aria-hidden="true" />
        </span>
        <h2 className="min-w-0 flex-1 font-semibold text-lg">{t(entity.labelKey)}</h2>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-semibold text-foreground text-lg tabular-nums">{tile.total}</span>
          {tile.viewAllTo && entity.viewAllAriaLabelKey ? (
            <ViewAction action={{ to: tile.viewAllTo, ariaLabel: t(entity.viewAllAriaLabelKey) }} />
          ) : (
            // Keeps tiles without a header link aligned with the ones that have it.
            <div className="invisible" aria-hidden="true">
              <WFPButton variant="link" size="sm" className="shrink-0 px-0" tabIndex={-1}>
                <ViewLabel />
              </WFPButton>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4 pl-17">
        {tile.subMetrics.map(metric => (
          <div key={metric.id} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 text-muted-foreground text-sm">{metric.label}</span>
            <span className="shrink-0 font-semibold text-base text-foreground tabular-nums">
              {metric.value}
            </span>
            {metric.action ? <ViewAction action={metric.action} /> : null}
          </div>
        ))}
      </div>
    </Card>
  );
}
